import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Portal from './Portal';
import { useOnEscape } from './overlayHooks';

/**
 * Popover — a floating panel anchored to a trigger. Handles open state,
 * outside-click and Escape dismissal.
 *
 * The panel is rendered in a Portal with fixed positioning computed from the
 * trigger's bounding box, so it is never clipped by scrollable / overflow:hidden
 * ancestors (e.g. a DataTable's horizontal scroll container).
 *
 * Props:
 *   trigger: ReactNode                          — the clickable anchor
 *   placement: 'bottom-start'|'bottom-end'|'top-start'|'top-end'  (default 'bottom-start')
 *   open / onOpenChange                         — optional controlled mode
 *   matchTriggerWidth: boolean                  — panel min-width = trigger width
 *   role: aria role for the panel (default 'dialog'); DropdownMenu passes 'menu'
 *   children: ReactNode | ({ close }) => ReactNode
 */
const GAP = 4;
const MARGIN = 8;

const Popover = ({
  trigger,
  placement = 'bottom-start',
  open: openProp,
  onOpenChange,
  matchTriggerWidth = false,
  role = 'dialog',
  className = '',
  panelClassName = '',
  children,
}) => {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [openState, setOpenState] = useState(false);
  const [coords, setCoords] = useState(null);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;

  const setOpen = useCallback((next) => {
    if (!isControlled) setOpenState(next);
    onOpenChange?.(next);
  }, [isControlled, onOpenChange]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  useOnEscape(open, close);

  // Outside-click that also accounts for the portaled panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const a = anchorRef.current;
      const p = panelRef.current;
      if ((a && a.contains(e.target)) || (p && p.contains(e.target))) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, close]);

  const computePosition = useCallback(() => {
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const panel = panelRef.current;
    const pw = panel?.offsetWidth || 0;
    const ph = panel?.offsetHeight || 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isBottom = placement.startsWith('bottom');
    const isEnd = placement.endsWith('end');

    let top = isBottom ? r.bottom + GAP : r.top - GAP - ph;
    // flip vertically if it would overflow and the opposite side has room
    if (isBottom && top + ph > vh - MARGIN && r.top - GAP - ph > MARGIN) {
      top = r.top - GAP - ph;
    } else if (!isBottom && top < MARGIN && r.bottom + GAP + ph < vh - MARGIN) {
      top = r.bottom + GAP;
    }

    let left = isEnd ? r.right - pw : r.left;
    if (matchTriggerWidth) left = r.left;
    left = Math.max(MARGIN, Math.min(left, vw - pw - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - ph - MARGIN));

    setCoords({ top, left, minWidth: matchTriggerWidth ? r.width : undefined });
  }, [placement, matchTriggerWidth]);

  useLayoutEffect(() => {
    if (!open) { setCoords(null); return undefined; }
    computePosition();
    const raf = requestAnimationFrame(computePosition);
    const onMove = () => computePosition();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, computePosition]);

  return (
    <span className={`ps-popover ${className}`.trim()}>
      <span
        ref={anchorRef}
        className="ps-popover__anchor"
        onClick={() => setOpen(!open)}
      >
        {React.isValidElement(trigger)
          ? React.cloneElement(trigger, {
              'aria-haspopup': role === 'menu' ? 'menu' : 'dialog',
              'aria-expanded': open,
            })
          : trigger}
      </span>
      {open && (
        <Portal>
          <div
            ref={panelRef}
            className={`ps-popover__panel ps-popover__panel--portal ps-popover__panel--${placement} ${panelClassName}`.trim()}
            role={role}
            style={{
              position: 'fixed',
              top: coords ? coords.top : -9999,
              left: coords ? coords.left : -9999,
              right: 'auto',
              bottom: 'auto',
              minWidth: coords?.minWidth,
              visibility: coords ? 'visible' : 'hidden',
            }}
          >
            {typeof children === 'function' ? children({ close }) : children}
          </div>
        </Portal>
      )}
    </span>
  );
};

export default Popover;
